import React from 'react';

/**
 * Indicateur visuel de l'état du média (audio/vidéo).
 * Affiché juste en dessous de la timeline GPS (maître).
 * 
 * - Barre de progression : zone grise (pas de média), bleue (disponible), verte (buffered)
 * - Curseur : position actuelle
 * - Volume : seul contrôle interactif
 */
const MediaIndicator = ({
    timelineState,
    slaveState,
    onVolumeChange,
    mediaType,
}) => {
    if (!mediaType || mediaType === 'gps') return null;

    const pos = Math.floor(slaveState?.position || 0);
    const dur = Math.floor(slaveState?.duration || 0);
    const volume = slaveState?.volume ?? 20;
    const mediaOffset = timelineState?.mediaOffset || 0;
    const mapMax = timelineState?.max || 0;
    const mapPos = timelineState?.value || 0;
    const displayText = slaveState?.display;
    const nativeBufferRanges = slaveState?.bufferRanges || [];
    const sessionBufferRanges = slaveState?.sessionBufferRanges;
    const bufferRanges = Array.isArray(sessionBufferRanges)
        ? sessionBufferRanges
        : nativeBufferRanges.map(range => ({
            start: mediaOffset + Number(range.start || 0),
            end: mediaOffset + Number(range.end || 0),
        }));
    const mediaRange = slaveState?.sessionMediaRange || {
        start: mediaOffset,
        end: mediaOffset + dur,
    };
    const icon = mediaType === 'video' ? '🎥' : '🎵';

    // Positions en % de la timeline GPS
    const toPercent = value => mapMax > 0
        ? Math.max(0, Math.min(100, (Number(value || 0) / mapMax) * 100))
        : 0;
    const mediaStartPct = toPercent(mediaRange.start);
    const mediaEndPct = toPercent(mediaRange.end);
    const cursorPct = mapMax > 0 ? Math.min(100, (mapPos / mapMax) * 100) : 0;

    return (
        <div className="media-indicator-block">
            <div className="timeline-stack media-status-stack">
              <div className="media-indicator-info">
                {displayText
                    ? <span className="media-indicator-pending">{displayText}</span>
                    : dur > 0
                        ? <span>{icon} {fmt(pos)} / {fmt(dur)}</span>
                        : <span className="media-indicator-loading">{icon} Chargement…</span>
                }
              </div>

              <div className="media-indicator-track">
                {/* Zone grise : pas de média */}
                {mediaStartPct > 0 && (
                    <div
                        className="media-indicator-zone mi-empty"
                        style={{ left: '0%', width: `${mediaStartPct}%` }}
                    />
                )}

                {/* Zone disponible (clip entier) */}
                {dur > 0 && (
                    <div
                        className="media-indicator-zone mi-available"
                        style={{ left: `${mediaStartPct}%`, width: `${Math.max(0, mediaEndPct - mediaStartPct)}%` }}
                    />
                )}

                {/* Zones bufferisées (prêtes à lire) */}
                {bufferRanges.map((range, i) => {
                    const bufStartPct = toPercent(range.start);
                    const bufEndPct = toPercent(range.end);
                    return (
                        <div
                            key={i}
                            className="media-indicator-zone mi-buffered"
                            style={{ left: `${bufStartPct}%`, width: `${Math.max(0, bufEndPct - bufStartPct)}%` }}
                        />
                    );
                })}

                {/* Curseur de position GPS */}
                <div
                    className="media-indicator-cursor"
                    style={{ left: `${cursorPct}%` }}
                />
              </div>
            </div>
            {/* Volume sous la durée */}
            <div className="media-indicator-volume">
                <span>🔊</span>
                <input
                    type="range"
                    min="0"
                    max="100"
                    value={volume}
                    className="custom-slider volume-slider"
                    aria-label="Volume"
                    onInput={(e) => onVolumeChange?.(parseFloat(e.target.value))}
                    style={{ '--value-percent': `${volume}%` }}
                />
            </div>
        </div>
    );
};

const fmt = (totalSec) => {
    const s = Math.floor(totalSec || 0);
    return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

export default MediaIndicator;
